import { defineStore } from 'pinia';
import { ref } from 'vue';

export type Alert = {
  id: string;
  title?: string;
  route?: { name: string; params?: Record<string, string>; query?: Record<string, string> };
  message: string;
  icon?: string;
  color: string;
  timeout?: number;
  alertClass?: string;
};

export const useAlertStore = defineStore('alert', () => {
  const alerts = ref<Alert[]>([]);

  function addSuccessAlert(
    message: string,
    title = 'notify.success',
    route?: { name: string; params?: Record<string, string>; query?: Record<string, string> }
  ) {
    const alert = {
      id: `${Math.floor(Math.random() * 100000)}-${Date.now()}`,
      title,
      message,
      route,
      color: 'success-150',
      alertClass: 'success',
      timeout: 5000
    };
    addNewAlert(alert);
  }

  function addInfoAlert(
    message: string,
    title = 'notify.info',
    route?: { name: string; params?: Record<string, string>; query?: Record<string, string> }
  ) {
    const alert = {
      id: `${Math.floor(Math.random() * 100000)}-${Date.now()}`,
      title,
      message,
      route,
      color: 'info-light',
      alertClass: 'info',
      timeout: 5000
    };
    addNewAlert(alert);
  }

  function addErrorAlert(
    message: string,
    title = 'notify.error',
    route?: { name: string; params?: Record<string, string>; query?: Record<string, string> }
  ) {
    const alert = {
      id: `${Math.floor(Math.random() * 100000)}-${Date.now()}`,
      title,
      message,
      route,
      color: 'error-100',
      alertClass: 'error'
    };
    addNewAlert(alert);
  }

  function addNewAlert(alert: Alert) {
    alerts.value = [alert, ...alerts.value];
    if (alert.timeout) {
      setTimeout(() => {
        removeAlert(alert.id);
      }, alert.timeout);
    }
  }

  function removeAlert(id: string) {
    alerts.value = alerts.value.filter((n: Alert): boolean => n.id !== id);
  }

  function removeErrorAlerts(): void {
    alerts.value = alerts.value.filter((alert: Alert): boolean => alert.alertClass !== 'error');
  }

  function alertExists(message: string): boolean {
    return alerts.value.some((alert: Alert): boolean => alert.message === message);
  }

  return { alerts, removeAlert, addSuccessAlert, addInfoAlert, addErrorAlert, removeErrorAlerts, alertExists };
});
